import React from "react";
import { connect } from "react-redux";
import { History } from "history";
import moment from "moment";
import _ from "lodash";
import { FormattedMessage, injectIntl } from "react-intl";

import Table from "components/Table";
import FieldChooser from "components/FieldChooser";
import MyDeliveriesSearch from "./partials/MyDeliveriesSearch";
import Button from "components/Button";
import { IMeta, ICommonState, IMyDelivery } from "models/.";
import { columns } from "./columns";
import MyDeliveryActions from "./actions";
import Swal from "helpers/swal";
import { URLS } from "constants/urlConstants";
import { Reorder } from "components/Icons";
import { CUSTOM_SETTINGS } from "constants/.";

interface IProps {
  getMyDeliveries: (meta?: IMeta) => void;
  initDeliveryNotes: (objects: IMyDelivery[], redirect: boolean) => void;
  meta: IMeta;
  myDeliveries: IMyDelivery[];
  history: History;
  settings: null | object;
  roles: string[];
  intl: any;
  roleIds: any[];
}

interface IState extends ICommonState {
  selectedDeliveries: IMyDelivery[];
}

class MyDeliveries extends React.Component<IProps, IState> {
  constructor(props) {
    super(props);

    this.state = {
      selectedDeliveries: [],
      showFieldChooser: false,
      columnsCopy: [],
      checkedRows: {}
    };
  }

  public handleFieldChooser = () => {
    this.setState(prevState => ({
      showFieldChooser: !prevState.showFieldChooser
    }));
  };

  public handleFieldsChange = data => {
    this.setState({ columnsCopy: data });
  };

  public onRowClick = rowData => {
    this.props.history.push(
      `${URLS.ORDERS_URL}/${rowData.OrderId}?kooptype=${rowData.RoleId}`
    );
  };

  private onRowSelect = async checkedRows => {
    const { selectedDeliveries: oldSelectedDeliveries } = this.state;
    const selectedIds = _(checkedRows)
      .keys()
      .map(_.toNumber)
      .value();
    let selectedDeliveries = _.cloneDeep(oldSelectedDeliveries);

    _.forEach(this.props.myDeliveries, item => {
      if (
        _.includes(selectedIds, item.Id) &&
        !_.find(oldSelectedDeliveries, ["Id", item.Id])
      ) {
        selectedDeliveries.push(item);
      }
    });
    selectedDeliveries = _.filter(selectedDeliveries, item =>
      _.includes(selectedIds, item.Id)
    );

    this.setState({ checkedRows, selectedDeliveries });
  };

  private initDeliveryNotes = async () => {
    const { checkedRows } = this.state;
    const {
      intl: { formatMessage },
      initDeliveryNotes
    } = this.props;

    if (_.isEmpty(checkedRows)) {
      Swal.alert("Info", formatMessage({ id: "DELIVERIES_MINIMUM_ONE_ORDER" }));
    } else {
      let isValid = true;
      const selectedDeliveries = _.filter(this.state.selectedDeliveries, item =>
        _(checkedRows)
          .keys()
          .includes("" + item.Id)
      );

      const isAllTheSame = key =>
        _.every(selectedDeliveries, [
          key,
          _.get(selectedDeliveries, `[0].${key}`)
        ]);
      const hasSameBuyer = isAllTheSame("Order.BuyerId");
      const hasSameBio = isAllTheSame("Bio");
      const hasSameYear = isAllTheSame("Order.HarvestYear");
      const hasRecentYears = _.every(
        selectedDeliveries,
        ({ Order: { HarvestYear } }) =>
          HarvestYear === moment().year() ||
          HarvestYear ===
            moment()
              .subtract(1, "year")
              .year()
      );
      const hasSameAddress = _.every(
        selectedDeliveries,
        ({ Order: { DeliveryAddress } }) =>
          _.get(DeliveryAddress, "Name") ===
            _.get(selectedDeliveries, "[0].Order.DeliveryAddress.Name") &&
          _.get(DeliveryAddress, "ZipCode") ===
            _.get(selectedDeliveries, "[0].Order.DeliveryAddress.ZipCode") &&
          _.get(DeliveryAddress, "City") ===
            _.get(selectedDeliveries, "[0].Order.DeliveryAddress.City") &&
          _.get(DeliveryAddress, "Country.ISOCode") ===
            _.get(
              selectedDeliveries,
              "[0].Order.DeliveryAddress.Country.ISOCode"
            )
      );

      if (!hasSameBuyer && isValid) {
        Swal.alert("Info", formatMessage({ id: "DELIVERIES_ONLY_ONE_BUYER" }));
        isValid = false;
      }
      if (!hasSameBio && isValid) {
        Swal.alert("Info", formatMessage({ id: "SAME_BIO" }));
        isValid = false;
      }
      if (!hasSameYear && isValid) {
        await Swal.confirm({
          title: formatMessage({ id: "CONFIRM_CONTINUE" }),
          text: formatMessage({ id: "DIFFERENT_HARVEST" })
        }).then(result => {
          if (result.dismiss) {
            isValid = false;
          }
        });
      }
      if (!hasRecentYears && isValid) {
        await Swal.confirm({
          title: formatMessage({ id: "CONFIRM_CONTINUE" }),
          text: formatMessage({ id: "OLD_HARVEST" })
        }).then(result => {
          if (result.dismiss) {
            isValid = false;
          }
        });
      }
      if (!hasSameAddress && isValid) {
        await Swal.confirm({
          title: formatMessage({ id: "CONFIRM_CONTINUE" }),
          text: formatMessage({ id: "MULTIPLE_ADDRESSES" })
        }).then(result => {
          if (result.dismiss) {
            isValid = false;
          }
        });
      }

      isValid && initDeliveryNotes(selectedDeliveries, true);
    }
  };

  public render() {
    const {
      myDeliveries,
      getMyDeliveries,
      settings,
      meta,
      roles,
      roleIds
    } = this.props;
    const { showFieldChooser, columnsCopy, checkedRows } = this.state;
    const filteredColumns = !_.includes(roles, "Orders")
      ? columns.filter(c => c.key !== "pdf")
      : columns;

    return (
      <div id="my-deliveries-page">
        <div className="child-header">
          <FormattedMessage id="MY_DELIVERIES" tagName="h1" />
          <Button
            id="create-delivery-top"
            kind="primary"
            className="animate-bg bg-white"
            onClick={this.initDeliveryNotes}
          >
            DELIVERIES_CREATE
          </Button>
        </div>
        <div className="delivery-search">
          <MyDeliveriesSearch
            meta={meta}
            onSearch={getMyDeliveries}
            settings={settings}
            roleIds={roleIds}
          />
        </div>
        <div className="field-chooser-container">
          <div className="open-field-chooser" onClick={this.handleFieldChooser}>
            <FormattedMessage id="CHOOSE_COLUMN" tagName="label" />
            <Reorder />
          </div>
        </div>
        <Table
          columns={columnsCopy}
          dataSource={myDeliveries}
          meta={meta}
          onQueryChange={getMyDeliveries}
          onRowClick={_.includes(roles, "Orders") ? this.onRowClick : undefined}
          onRowSelect={this.onRowSelect}
          checkedRows={checkedRows}
          groupBy="Order.Buyer.Name"
          pageName={CUSTOM_SETTINGS.deliveryNotes}
        />
        <FieldChooser
          name="mijn_leveringen"
          visible={showFieldChooser}
          handleFieldChooser={this.handleFieldChooser}
          handleFieldsChange={this.handleFieldsChange}
          columns={filteredColumns}
        />
        <Button
          id="create-delivery-top"
          kind="primary"
          className="animate-bg bg-white delivery-create"
          onClick={this.initDeliveryNotes}
        >
          DELIVERIES_CREATE
        </Button>
      </div>
    );
  }
}

export default connect(
  state => ({
    roles: state.app.roles,
    settings: state.preferences.settings,
    myDeliveries: state.myDeliveries.items,
    meta: state.myDeliveries.meta,
    roleIds: state.app.roleIds
  }),
  {
    getMyDeliveries: MyDeliveryActions.getMyDeliveries,
    initDeliveryNotes: MyDeliveryActions.initDeliveryNotes
  }
)(injectIntl(MyDeliveries));
